import type { Mensagem, MensagemCanal, MensagemMidiaTipo } from '@/features/messaging';
import type { NegociacaoCasoStatus } from '@/features/negociacoes';
import {
  AVISO_NEGOCIACAO_ARQUIVADA,
  AVISO_NEGOCIACAO_CONCLUIDA,
} from '@/shared/constants/envioDesativadoNegociacao';
import { AVISO_AGUARDANDO_RETOMADA } from '@/shared/constants/janelaAtendimento';
import type { TipoEncerramento } from '@/features/negociacoes/types/encerramentoTypes';
import {
  cadenceToTemperature,
  closedStatusLabelMap,
  closedStatusVariantMap,
  statusLabelMap,
  statusVariantMap,
} from '@/features/negociacoes/utils';
import { formatDate as formatDateShared, formatDateTimeFull } from '@/shared/formatters';
import { truncateText } from '@/shared/utils';
import { chatMessageButtons } from '@/shared/utils/chatMessageButtons';
import { extensaoDoNome } from '@/shared/utils/detectarFormato';
import type {
  JanelaAtendimento,
  NegociacaoCasoDetail,
  StatusJanela,
} from '../types/negociacaoDetailTypes';
import type {
  ChatMessageUi,
  IaStatusUi,
  LastMensagemUi,
  NegotiationDetails,
  NegotiationSummaryUi,
  SituacaoAtualUi,
} from '../types/negociacaoDetailUiTypes';
import { rotuloAtendente } from './autoriaMensagem';

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  return formatDateShared(value) || value;
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  return formatDateTimeFull(value) || value;
};

const getConfidenceLevel = (value: number): 'Alta' | 'Média' | 'Baixa' => {
  if (value >= 70) return 'Alta';
  if (value >= 40) return 'Média';
  return 'Baixa';
};

export const getTemperatureColorClass = (temperature: string): string => {
  switch (temperature) {
    case 'Quente':
      return 'text-error-600';
    case 'Morna':
      return 'text-warning-600';
    case 'Fria':
      return 'text-information-600';
    default:
      return 'text-subtext-color';
  }
};

const isMensagemManual = (mensagem: Mensagem): boolean => {
  const metadata = mensagem.metadata;
  if (!metadata) return false;
  const isManual = (metadata as Record<string, unknown>).isManual;
  return isManual === true;
};

// `senderId` é o que identifica a pessoa. `isManual` sozinho não basta: o HSM de retomada enviado
// pelo atendimento não o marca, e sem o `senderId` ele cairia na conta da Lexi.
const isMensagemDeAtendente = (mensagem: Mensagem): boolean =>
  Boolean(mensagem.senderId) || isMensagemManual(mensagem);

const ROTULO_IA = 'IA (Agente)';

const rotuloRemetente = (
  mensagem: Mensagem,
  clientName: string,
  usuarioAtualId: string | null,
): string => {
  if (mensagem.direction !== 'OUTBOUND') return clientName;
  if (!isMensagemDeAtendente(mensagem)) return ROTULO_IA;
  return rotuloAtendente({
    autorId: mensagem.senderId ?? null,
    autorNome: mensagem.senderNome ?? null,
    usuarioAtualId,
  });
};

export const isNotaInternaDoSistema = (mensagem: Pick<Mensagem, 'tipo'>): boolean =>
  mensagem.tipo === 'SYSTEM';

export const isMensagemDeMidia = (
  mensagem: Mensagem,
): mensagem is Mensagem & { tipo: MensagemMidiaTipo } =>
  mensagem.tipo === 'DOCUMENT' ||
  mensagem.tipo === 'IMAGE' ||
  mensagem.tipo === 'AUDIO' ||
  mensagem.tipo === 'VIDEO';

/**
 * Extensão do arquivo apontado por uma URL absoluta, em minúsculas e sem a query string.
 * É o parse único da feature: nomeia o documento do acordo e as mídias do chat.
 * URL inválida ou caminho sem ponto devolve `''`.
 */
export function extrairExtensao(url: string): string {
  try {
    return extensaoDoNome(new URL(url).pathname);
  } catch {
    // Object key cru ou URL relativa não têm extensão confiável; o chamador decide o nome sem ela.
    return '';
  }
}

export const getMensagemCaseId = (mensagem: Mensagem): string | null => {
  const metadata = mensagem.metadata;
  if (!metadata || typeof metadata !== 'object') return null;
  const caseId = (metadata as Record<string, unknown>).caseId;
  return typeof caseId === 'string' && caseId.trim() ? caseId : null;
};

const getMensagemPreview = (mensagem: Mensagem): string => {
  const rawOriginal = (mensagem.textBody || '').trim();
  const raw = rawOriginal
    ? rawOriginal
        .replace(/^"+|"+$/g, '')
        .replace(/^\{+|\}+$/g, '')
        .trim()
    : '';

  const templateMatch = raw.match(/template\s*:\s*([a-z0-9._-]+)/i);
  if (templateMatch) {
    return 'Mensagem automática (template).';
  }

  if (rawOriginal) return truncateText(raw, 120);

  if (mensagem.tipo === 'AUDIO') return 'Mensagem de áudio.';
  if (mensagem.tipo === 'IMAGE') return 'Imagem enviada.';
  if (mensagem.tipo === 'DOCUMENT') return 'Documento enviado.';
  if (mensagem.tipo === 'VIDEO') return 'Vídeo enviado.';

  return 'Mensagem sem conteúdo.';
};

const getMensagemBadge = (
  mensagem: Mensagem,
): { badgeVariant: 'error' | 'success' | 'warning' | 'brand' | 'neutral'; badgeLabel: string } => {
  if (mensagem.direction === 'INBOUND') {
    return { badgeVariant: 'warning', badgeLabel: 'Recebida' };
  }

  if (mensagem.status === 'FAILED') {
    return { badgeVariant: 'error', badgeLabel: 'Falhou' };
  }

  if (mensagem.status === 'PENDING') {
    return { badgeVariant: 'warning', badgeLabel: 'Pendente' };
  }

  if (mensagem.status === 'SEEN') {
    return { badgeVariant: 'success', badgeLabel: 'Visualizada' };
  }

  if (mensagem.status === 'DELIVERED') {
    return { badgeVariant: 'brand', badgeLabel: 'Entregue' };
  }

  return { badgeVariant: 'brand', badgeLabel: 'Enviada' };
};

const isMensagemAfterCaseCreation = (
  mensagem: Mensagem,
  caseCreatedAt: string | null | undefined,
): boolean => {
  if (!caseCreatedAt) return true;

  const caseTimestamp = new Date(caseCreatedAt).getTime();
  if (Number.isNaN(caseTimestamp)) return true;

  const mensagemTimestamp = new Date(mensagem.createdAt).getTime();
  if (Number.isNaN(mensagemTimestamp)) return true;

  return mensagemTimestamp >= caseTimestamp;
};

const isClosedStatus = (status: NegociacaoCasoStatus | undefined): boolean => {
  return status === 'CLOSED' || status === 'AGREEMENT_CLOSED' || status === 'NO_AGREEMENT';
};

export const isReadOnlyStatus = (status: NegociacaoCasoStatus | undefined): boolean => {
  return status === 'AGREEMENT_CLOSED' || status === 'NO_AGREEMENT' || status === 'CLOSED';
};

export const getSendDisabledReason = ({
  isScheduled,
  isReadOnly,
  isArchived,
  conversaId,
  janelaAguardandoRetomada,
}: {
  isScheduled: boolean;
  isReadOnly: boolean;
  isArchived: boolean;
  conversaId: string | null;
  janelaAguardandoRetomada: boolean;
}): string | null => {
  if (isScheduled) {
    return 'Negociação agendada. Aguarde o início para enviar mensagens.';
  }

  if (isArchived) {
    return AVISO_NEGOCIACAO_ARQUIVADA;
  }

  if (isReadOnly) {
    return AVISO_NEGOCIACAO_CONCLUIDA;
  }

  if (!conversaId) {
    return 'Conversa indisponível no momento.';
  }

  if (janelaAguardandoRetomada) {
    return AVISO_AGUARDANDO_RETOMADA;
  }

  return null;
};

export const isJanelaBloqueada = (status: StatusJanela | null | undefined): boolean =>
  status === 'EXPIRADA' || status === 'AGUARDANDO_RETOMADA';

export const isJanelaAguardandoRetomada = (status: StatusJanela | null | undefined): boolean =>
  status === 'AGUARDANDO_RETOMADA';

export const canSendResumeTemplate = ({
  status,
  archivedAt,
  conversaId,
  janelaStatus,
}: {
  status: NegociacaoCasoStatus | undefined;
  archivedAt: string | null | undefined;
  conversaId: string | null;
  janelaStatus: StatusJanela | null;
}): boolean => {
  const isOngoing = status === 'ACTIVE' || status === 'NEGOTIATION_STARTED' || status === 'PAUSED';
  return isOngoing && !archivedAt && Boolean(conversaId) && janelaStatus === 'EXPIRADA';
};

export const msUntilJanelaExpiry = (
  status: StatusJanela | null | undefined,
  expiraEm: string | null | undefined,
  now = Date.now(),
): number | null => {
  if (status !== 'ABERTA' || !expiraEm) return null;
  const at = Date.parse(expiraEm);
  if (!Number.isFinite(at)) return null;
  return Math.max(0, at - now);
};

export const isJanelaSemPrimeiraResposta = (
  janela: { lastInboundAt?: JanelaAtendimento['lastInboundAt'] } | null | undefined,
): boolean => !janela?.lastInboundAt;

const resolveNegotiationChannel = (
  canal: MensagemCanal | undefined,
): 'WhatsApp' | 'E-mail' | 'SMS' | 'Multicanal' => {
  if (canal === 'EMAIL') return 'E-mail';
  if (canal === 'SMS') return 'SMS';
  return 'WhatsApp';
};

export const buildNegotiationSummaryUi = (
  caso: NegociacaoCasoDetail | null | undefined,
): NegotiationSummaryUi | null => {
  if (!caso) return null;

  const temperature = cadenceToTemperature(caso.cadenceBucket);
  const status = caso.status as NegociacaoCasoStatus;
  const isClosedStatus =
    status === 'AGREEMENT_CLOSED' || status === 'NO_AGREEMENT' || status === 'CLOSED';

  const isArchived = Boolean(caso.archivedAt);
  const tipoEncerramento = caso.encerramento?.tipo as TipoEncerramento | undefined;
  const statusLabel = isArchived
    ? 'Arquivada'
    : isClosedStatus && tipoEncerramento && tipoEncerramento in closedStatusLabelMap
      ? closedStatusLabelMap[tipoEncerramento]
      : statusLabelMap[status];
  const statusVariant = isArchived
    ? 'neutral'
    : isClosedStatus && tipoEncerramento && tipoEncerramento in closedStatusVariantMap
      ? closedStatusVariantMap[tipoEncerramento]
      : statusVariantMap[status];

  return {
    id: caso.id,
    negotiationName: caso.titulo,
    client: caso.counterpartyName,
    status: statusLabel,
    statusValue: status,
    statusVariant: statusVariant,
    temperature,
    isPaused: caso.status === 'PAUSED',
    isScheduled: caso.status === 'SCHEDULED',
    updatedAt: caso.dataAtualizacao,
    createdAt: caso.dataCriacao,
  };
};

export const buildNegotiationDetails = ({
  caso,
  negotiationSummaryUi,
  channel,
  strategy,
  recommendation,
  flowId,
}: {
  caso: NegociacaoCasoDetail | null | undefined;
  negotiationSummaryUi: NegotiationSummaryUi | null;
  channel: MensagemCanal | undefined;
  strategy: NegotiationDetails['strategy'];
  recommendation: NegotiationDetails['recommendation'];
  flowId: string | null;
}): NegotiationDetails | null => {
  if (!caso || !negotiationSummaryUi) return null;

  const type = caso.isBulk ? 'Lote' : 'Individual';
  const agent = caso.scenarioType || 'Negociação';
  const confidence = Math.min(100, Math.max(0, Math.round(caso.heat ?? 0)));

  return {
    id: caso.id,
    numeroNegociacao: caso.numeroNegociacao,
    client: negotiationSummaryUi.client,
    counterpartyPhone: caso.counterpartyPhone,
    counterpartyCpfCnpj: caso.counterpartyCpfCnpj,
    negotiationName: negotiationSummaryUi.negotiationName,
    flowId,
    type,
    agent,
    objective: agent,
    temperature: negotiationSummaryUi.temperature,
    status: negotiationSummaryUi.status,
    statusVariant: negotiationSummaryUi.statusVariant,
    atendimentoHumanoAtivo: caso.atendimentoHumanoAtivo,
    isPaused: negotiationSummaryUi.isPaused,
    isScheduled: negotiationSummaryUi.isScheduled,
    createdAt: formatDate(caso.dataCriacao),
    createdTime: formatDateTime(caso.dataCriacao),
    updatedAt: formatDate(caso.dataAtualizacao),
    updatedTime: formatDateTime(caso.dataAtualizacao),
    channel: resolveNegotiationChannel(channel),
    confidence,
    confidenceLevel: getConfidenceLevel(confidence),
    strategy,
    recommendation,
    customFieldValues: caso.customFieldValues ?? {},
  };
};

export const filterMensagensForCaso = ({
  casoId,
  caseCreatedAt,
  mensagens,
}: {
  casoId: string | undefined;
  caseCreatedAt: string | null | undefined;
  mensagens: Mensagem[];
}): Mensagem[] => {
  // A nota interna vive na conversa, não na negociação: sem este corte ela aparecia como mensagem
  // da Lexi no chat, como "última mensagem" e como documento sem nome na aba Documentos.
  const semNotasInternas = mensagens.filter((mensagem) => !isNotaInternaDoSistema(mensagem));
  if (!casoId) return semNotasInternas;

  return semNotasInternas.filter((mensagem) => {
    const mensagemCaseId = getMensagemCaseId(mensagem);
    if (mensagemCaseId && mensagemCaseId !== casoId) {
      return false;
    }

    return isMensagemAfterCaseCreation(mensagem, caseCreatedAt);
  });
};

export const filterMensagensForChat = (mensagens: Mensagem[]): Mensagem[] => {
  return mensagens.filter((mensagem) => {
    const hasText = Boolean((mensagem.conteudoVisivel ?? mensagem.textBody ?? '').trim());
    const hasOptions =
      mensagem.direction === 'OUTBOUND' && chatMessageButtons(mensagem.metadata) !== null;
    return hasText || hasOptions || Boolean(mensagem.mediaUrl) || isMensagemDeMidia(mensagem);
  });
};

export const sortMensagensByCreatedAt = (mensagens: Mensagem[]): Mensagem[] => {
  return [...mensagens].sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
  );
};

export const mapChatMessages = ({
  mensagens,
  clientName,
  usuarioAtualId,
}: {
  mensagens: Mensagem[];
  clientName: string;
  usuarioAtualId: string | null;
}): ChatMessageUi[] => {
  return mensagens.map((mensagem) => {
    const isOutbound = mensagem.direction === 'OUTBOUND';

    return {
      id: mensagem.id,
      sender: rotuloRemetente(mensagem, clientName, usuarioAtualId),
      text: mensagem.conteudoVisivel ?? mensagem.textBody ?? '',
      metadata: mensagem.metadata,
      time: formatDateTime(mensagem.createdAt),
      isIA: isOutbound && !isMensagemDeAtendente(mensagem),
      isOutbound,
      type: mensagem.tipo,
      mediaUrl: mensagem.mediaUrl,
      status: mensagem.status,
    };
  });
};

export const getLastMensagem = (mensagens: Mensagem[]): Mensagem | null => {
  if (!mensagens.length) return null;

  return mensagens.reduce((latest, current) => {
    const latestTimestamp = new Date(latest.createdAt).getTime();
    const currentTimestamp = new Date(current.createdAt).getTime();
    return currentTimestamp > latestTimestamp ? current : latest;
  }, mensagens[0]);
};

export const buildLastMensagemUi = ({
  lastMensagem,
  clientName,
  usuarioAtualId,
}: {
  lastMensagem: Mensagem | null;
  clientName: string;
  usuarioAtualId: string | null;
}): LastMensagemUi => {
  if (!lastMensagem) {
    return {
      sender: '—',
      preview: 'Nenhuma mensagem ainda.',
      time: '-',
      badgeVariant: 'neutral',
      badgeLabel: 'Sem mensagens',
      isOutbound: false,
      temMensagem: false,
    };
  }

  const isOutbound = lastMensagem.direction === 'OUTBOUND';
  const sender = rotuloRemetente(lastMensagem, clientName, usuarioAtualId);
  const preview = getMensagemPreview(lastMensagem);
  const time = formatDateTime(lastMensagem.createdAt);
  const { badgeVariant, badgeLabel } = getMensagemBadge(lastMensagem);

  return {
    sender,
    preview,
    time,
    badgeVariant,
    badgeLabel,
    isOutbound,
    temMensagem: true,
  };
};

export const buildSituacaoAtualUi = ({
  isStartFailed,
  isStartCancelled,
  isStartPending,
  startScheduledLabel,
  isIAPaused,
  lastMensagem,
}: {
  isStartFailed: boolean;
  isStartCancelled: boolean;
  isStartPending: boolean;
  startScheduledLabel: string | null;
  isIAPaused: boolean;
  lastMensagem: Mensagem | null;
}): SituacaoAtualUi => {
  if (isStartFailed) {
    return {
      iconBgClass: 'bg-error-50',
      iconTextClass: 'text-error-600',
      message: 'Falha ao iniciar a negociação. Verifique o fluxo antes de retomar.',
    };
  }

  if (isStartCancelled) {
    return {
      iconBgClass: 'bg-neutral-50',
      iconTextClass: 'text-neutral-600',
      message: 'Início cancelado. A negociação não será iniciada automaticamente.',
    };
  }

  if (isStartPending) {
    return {
      iconBgClass: 'bg-warning-50',
      iconTextClass: 'text-warning-600',
      message: startScheduledLabel
        ? `Negociação agendada para ${startScheduledLabel}.`
        : 'Negociação agendada. Aguardando disparo automático.',
    };
  }

  if (isIAPaused) {
    return {
      iconBgClass: 'bg-warning-50',
      iconTextClass: 'text-warning-600',
      message: 'IA pausada — aguardando intervenção humana.',
    };
  }

  if (!lastMensagem) {
    return {
      iconBgClass: 'bg-neutral-50',
      iconTextClass: 'text-neutral-600',
      message: 'Sem interações registradas até o momento.',
    };
  }

  if (lastMensagem.direction === 'INBOUND') {
    return {
      iconBgClass: 'bg-brand-50',
      iconTextClass: 'text-brand-600',
      message: 'Mensagem recebida — aguardando próxima ação.',
    };
  }

  return {
    iconBgClass: 'bg-neutral-50',
    iconTextClass: 'text-neutral-600',
    message: 'Aguardando resposta da contraparte.',
  };
};

export const buildIaStatusUi = ({
  isStartFailed,
  isStartCancelled,
  isStartPending,
  startScheduledLabel,
  isIAPaused,
}: {
  isStartFailed: boolean;
  isStartCancelled: boolean;
  isStartPending: boolean;
  startScheduledLabel: string | null;
  isIAPaused: boolean;
}): IaStatusUi => {
  if (isStartFailed) {
    return {
      bannerClass: 'bg-error-50 border-error-100',
      iconClass: 'bg-error-100 text-error-700',
      dotClass: 'bg-error-500',
      title: 'Início falhou',
      message: 'Não foi possível iniciar a negociação automaticamente.',
      iconKey: 'FAILED',
    };
  }

  if (isStartCancelled) {
    return {
      bannerClass: 'bg-neutral-50 border-neutral-100',
      iconClass: 'bg-neutral-100 text-neutral-700',
      dotClass: 'bg-neutral-400',
      title: 'Início cancelado',
      message: 'O início automático foi cancelado.',
      iconKey: 'CANCELLED',
    };
  }

  if (isStartPending) {
    return {
      bannerClass: 'bg-warning-50 border-warning-100',
      iconClass: 'bg-warning-100 text-warning-700',
      dotClass: 'bg-warning-500',
      title: 'Início agendado',
      message: startScheduledLabel
        ? `Negociação agendada para ${startScheduledLabel}.`
        : 'Negociação agendada. Aguardando disparo automático.',
      iconKey: 'PENDING',
    };
  }

  if (isIAPaused) {
    return {
      bannerClass: 'bg-warning-50 border-warning-100',
      iconClass: 'bg-warning-100 text-warning-700',
      dotClass: 'bg-warning-500',
      title: 'IA Pausada',
      message: 'Você assumiu o controle. A IA não responderá novas mensagens.',
      iconKey: 'PAUSED',
    };
  }

  return {
    bannerClass: 'bg-brand-50 border-brand-100',
    iconClass: 'bg-brand-100 text-brand-700',
    dotClass: 'bg-success-500 animate-pulse',
    title: 'IA Ativa',
    message: 'O agente está conduzindo a conversa automaticamente.',
    iconKey: 'ACTIVE',
  };
};

export const getIsClosed = (status: NegociacaoCasoStatus | undefined): boolean => {
  return isClosedStatus(status);
};
